/** @file a library of styles that can be passed into motion components. */

/** Use to animate out when deleting something. */
const deleteMotion = {
  animate: { height: "auto", opacity: 1 },
  exit: { height: "0px", opacity: 0, overflow: "hidden" },
};

/** Use to animate out when deleting something. */
const inOutMotion = {
  initial: { height: "0px", opacity: 0, overflow: "hidden" },
  animate: { height: "auto", opacity: 1, overflow: "default" },
  exit: { height: "0px", opacity: 0, overflow: "hidden" },
  transition: { duration: 0.3 },
};

const slideUp = {
  initial: { transform: "translateY(-100%)", opacity: 0 },
  animate: { transform: "translateY(0%)", opacity: 1 },
  exit: {
    transform: "translateY(100%)",
    opacity: 0,
    transition: { duration: 0.15 },
  },
};

const slideDown = {
  initial: { transform: "translateY(100%)", opacity: 0 },
  animate: { transform: "translateY(0%)", opacity: 1 },
  exit: {
    transform: "translateY(-100%)",
    opacity: 0,
    transition: { duration: 0.15 },
  },
};

const inOutXSquish = {
  initial: { transform: "scaleX(0)", transformOrigin: "left" },
  animate: { transform: "scaleX(1)", transformOrigin: "left" },
  exit: {
    transform: "scaleX(0)",
    transformOrigin: "left",
    transition: { ease: "easeOut" },
  },
};

const explode = {
  initial: {
    scaleX: 0,
    scaleY: 0,
  },
  animate: {
    scaleX: 1,
    scaleY: 1,
    rotate: 0,
    transition: {
      type: "spring",
      restDelta: 0.001,
      restSpeed: 0.001,
      mass: 0.6,
      stiffness: 200,
      damping: 5,
    },
  },
  exit: {
    scaleX: 0,
    scaleY: 0,
  },
};

const explodeFromPoint = {
  initial: { ...explode.initial, transformOrigin: "left" },
  animate: { ...explode.animate, transformOrigin: "left" },
  exit: { ...explode.exit, transformOrigin: "left" },
};

const dangle = {
  initial: { rotate: -15 },
  animate: {
    rotate: 5,
    transition: { type: "spring", mass: 2, delay: 0.2, damping: 5 },
  },
  exit: { rotate: -15, transition: { duration: 0.6 } },
};

const straighten = {
  initial: { rotate: 0 },
  animate: {
    rotate: 0,
  },
  exit: { rotate: 0, transition: { duration: 0.6 } },
};

const inOutZoom = {
  initial: {
    height: "0px",
    opacity: 0,
    overflow: "hidden",
    scale: 0,
    transformOrigin: "top",
  },
  animate: {
    height: "auto",
    opacity: 1,
    transformOrigin: "top",
    scale: 1,
    overflow: "visible",
  },
  exit: {
    height: "0px",
    opacity: 0,
    overflow: "hidden",
    transformOrigin: "top",
    scale: 0,
  },
  transition: { ease: "easeInOut" },
};

/**
 * For when you don't neeed it centered, but you do want motion on the x axis
 * @param {object} param0
 * @param {number} param0.gapCompensation - If using a gap, use this to offset the gap applied to the element you're animating
 * @returns
 */
const inOutZoomAndWidth = ({ gapCompensation = 0 } = {}) => ({
  initial: {
    ...inOutZoom.initial,
    width: "0px",
    transformOrigin: "center",
    opacity: 0,
    margin: -(gapCompensation / 2),
  },
  animate: {
    ...inOutZoom.animate,
    width: "auto",
    transformOrigin: "center",
    opacity: 1,
    margin: 0,
  },
  exit: {
    ...inOutZoom.exit,
    width: "0px",
    transformOrigin: "center",
    opacity: 0,
    margin: -(gapCompensation / 2),
  },
});

const opacityMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const heightMotion = {
  initial: { height: 0 },
  animate: { height: "auto" },
  exit: { height: 0 },
};

export {
  deleteMotion,
  inOutMotion,
  opacityMotion,
  inOutXSquish,
  inOutZoom,
  inOutZoomAndWidth,
  explode,
  explodeFromPoint,
  dangle,
  straighten,
  slideUp,
  slideDown,
  heightMotion,
};
